import {
  WorkoutExercise,
  WorkoutExerciseMetric,
  WorkoutSet,
} from '@strength/domain-workout';
import { SectionExercises } from '../types';
import {
  MetricData,
  SetData,
  SetMetricData,
  ExerciseFormData,
  ExerciseOrder,
} from './components/create-edit-exercise-sheet/types';
import {
  ModifiedPublishedWorkoutLayerMutationsFns,
  WorkoutLayerEdits,
} from '../modified-published-mutations';
import { ExerciseMutationData } from '@strength/domain-workout';
import { NEW_EXERCISE_WORKOUT_ID } from './components/create-edit-exercise-sheet/TabletCreateEditExerciseSheet';
import { sortByOrder } from '@strength/utils';

// TODO: sc-302 - remove this once we have a way to get the metrics for collection
const METRICS_FOR_COLLECTION = [
  'Reps',
  'Intensity',
  'Average Velocity',
  'Peak Velocity',
  'Average Power',
  'Peak Power',
  'RPE',
  'Calories',
  'Distance',
  'Duration',
];

export const getSetsEdits = (
  modifiedPublishedMutationsFns: ModifiedPublishedWorkoutLayerMutationsFns,
  setData: SetData[],
  workout: SectionExercises,
  editWorkoutExercise: WorkoutExercise,
): WorkoutLayerEdits => {
  const { toAddSetMetrics, toEditSetMetrics, toRemoveSetMetrics } = compareSets(
    setData,
    editWorkoutExercise.sets,
  );
  const modifiedEdits: WorkoutLayerEdits['modifiedEdits'] = [];
  const draftEdits: WorkoutLayerEdits['draftEdits'] = [];

  if (toEditSetMetrics.length > 0) {
    toEditSetMetrics.forEach((set) => {
      const edits = modifiedPublishedMutationsFns.onEditSetMetric(
        workout.context,
        workout.workoutId,
        set.setId ?? '',
        {
          metricId: set.metricId,
          input: set.input,
          forCollection: set.forCollection,
        },
      );
      modifiedEdits.push(...edits.modifiedEdits);
      draftEdits.push(...edits.draftEdits);
    });
  }
  if (toAddSetMetrics.length > 0) {
    toAddSetMetrics.forEach((set) => {
      const edits = modifiedPublishedMutationsFns.onAddSetMetric(
        workout.context,
        workout.workoutId,
        editWorkoutExercise.workoutExerciseId,
        set.sortOrder,
        set.metrics,
      );
      modifiedEdits.push(...edits.modifiedEdits);
      draftEdits.push(...edits.draftEdits);
    });
  }
  if (toRemoveSetMetrics.length > 0) {
    // Combine removals that are for the same set group. If the number of removals
    // is less than the total number of sets, then we need to create an update edit;
    // otherwise, we create a removal edit
    const removalsByGroup = new Map<string, WorkoutSet[]>();
    toRemoveSetMetrics.forEach((set) => {
      const group = removalsByGroup.get(set.workoutSetGroupId) ?? [];
      group.push(set);
      removalsByGroup.set(set.workoutSetGroupId, group);
    });

    removalsByGroup.forEach((sets, groupId) => {
      const originalSets = sortByOrder(
        editWorkoutExercise.sets.filter(
          (set) => set.workoutSetGroupId === groupId,
        ),
      );

      // The first set is always the "real" set in the group (the one created on the draft layer)
      // make sure we grab this from the full list of sets; not just the removals
      const firstSet = originalSets[0];
      if (!firstSet) return;

      const edits = modifiedPublishedMutationsFns.onRemoveSetMetric(
        workout.context,
        workout.workoutId,
        firstSet.workoutSetId,
        originalSets.length,
        sets.map((set) => set.workoutSetId),
      );
      modifiedEdits.push(...edits.modifiedEdits);
      draftEdits.push(...edits.draftEdits);
    });
  }
  return { modifiedEdits, draftEdits };
};

export const getMetricsEdits = (
  modifiedPublishedMutationsFns: ModifiedPublishedWorkoutLayerMutationsFns,
  metricsData: MetricData[],
  existingMetrics: WorkoutExerciseMetric[],
  workout: SectionExercises,
  editWorkoutExercise: WorkoutExercise,
): WorkoutLayerEdits => {
  const { toAddMetrics, toRemoveMetrics } = compareMetrics(
    metricsData,
    existingMetrics,
  );
  const modifiedEdits: WorkoutLayerEdits['modifiedEdits'] = [];
  const draftEdits: WorkoutLayerEdits['draftEdits'] = [];

  if (toAddMetrics.length > 0) {
    toAddMetrics.forEach((metric) => {
      const edits = modifiedPublishedMutationsFns.onAddMetric(
        workout.context,
        workout.workoutId,
        editWorkoutExercise.workoutExerciseId,
        metric,
      );
      modifiedEdits.push(...edits.modifiedEdits);
      draftEdits.push(...edits.draftEdits);
    });
  }
  if (toRemoveMetrics.length > 0) {
    toRemoveMetrics.forEach((metric) => {
      const edits = modifiedPublishedMutationsFns.onRemoveMetric(
        workout.context,
        workout.workoutId,
        metric.workoutExerciseMetricId,
      );
      modifiedEdits.push(...edits.modifiedEdits);
      draftEdits.push(...edits.draftEdits);
    });
  }
  return { modifiedEdits, draftEdits };
};

export const getIsEachSideEdits = (
  modifiedPublishedMutationsFns: ModifiedPublishedWorkoutLayerMutationsFns,
  isEachSide: boolean,
  workout: SectionExercises,
  editWorkoutExercise: WorkoutExercise,
): WorkoutLayerEdits => {
  if (isEachSide === editWorkoutExercise.isEachSide) {
    return { modifiedEdits: [], draftEdits: [] };
  }

  return modifiedPublishedMutationsFns.onEditExercise(
    workout.context,
    workout.workoutId,
    editWorkoutExercise.workoutExerciseId,
    {
      isEachSide,
    },
  );
};

export const parseExerciseData = (
  exerciseData: ExerciseFormData,
  editExerciseId?: string,
) => {
  const { exercise, metrics, exerciseOrder, isEachSide } = exerciseData;

  // If there are multiple workouts on the same day for a user, we merge the workouts together
  // Because of this we will add the exercise to the workout based on the section it was added to
  const { newWorkoutSection, exerciseSortOrderEntry } =
    findNewWorkoutSectionAndEntry(exerciseOrder, editExerciseId);

  const data: ExerciseMutationData = {
    sortOrder: exerciseSortOrderEntry?.exerciseSortOrder ?? 0,
    libraryExerciseId: exercise.id,
    exerciseName: exercise.name,
    instruction: null,
    isEachSide,
  };

  const metricsData: MetricData[] = metrics.map(
    ({
      metricId,
      sortOrder,
      type: metricType,
      name: metricName,
      exerciseMetricId,
    }) => ({
      metricId,
      sortOrder,
      metricType,
      metricName,
      exerciseMetricId,
    }),
  );

  const setData: SetData[] = [];

  const numberOfSets = metrics[0]?.inputsForSet.length ?? 0;
  for (let i = 0; i < numberOfSets; i++) {
    let setId = '';
    let workoutSetGroupId = '';
    const metricsForSet = metrics.map((metric) => {
      const inputForSet = metric.inputsForSet[i];
      setId = inputForSet?.setId ?? '';
      workoutSetGroupId = inputForSet?.workoutSetGroupId ?? '';
      return {
        metricId: metric.metricId,
        input: inputForSet?.unit
          ? `${inputForSet.value}${inputForSet.unit}`
          : (inputForSet?.value ?? ''),
        forCollection: METRICS_FOR_COLLECTION.includes(metric.name),
      };
    });

    setData.push({
      sortOrder: i,
      setId: setId,
      workoutSetGroupId,
      metrics: metricsForSet,
    });
  }

  return {
    data,
    metricsData,
    setData,
    newWorkoutSection,
    exerciseSortOrderEntry,
  };
};

// compare set metric values between the workouts to determine what set metric edits need to be made
// CREATE: if setId is undefined
// UPDATE: if setId exists and both setData and existingSets and have different inputs
// DELETE: if the setId in existingSets doesn't exist in setData
export const compareSets = (setData: SetData[], existingSets: WorkoutSet[]) => {
  const toAddSetMetrics: SetData[] = [];
  const toEditSetMetrics: SetMetricData[] = [];
  const toRemoveSetMetrics: WorkoutSet[] = [];

  const existingSetsMap = new Map(existingSets.map((s) => [s.workoutSetId, s]));

  setData.forEach((set) => {
    const { setId, metrics } = set;
    const existingSet = existingSetsMap.get(setId ?? '');

    if (!setId) {
      toAddSetMetrics.push(set);
    } else if (existingSet) {
      // Compare metrics within the set
      const existingMetricsMap = new Map(
        existingSet.metrics.map((m) => [m.metricId, m]),
      );

      metrics.forEach((metric) => {
        const existingMetric = existingMetricsMap.get(metric.metricId);

        if (!existingMetric || existingMetric.input !== metric.input) {
          // Collect each changed metric individually
          toEditSetMetrics.push({
            setId: set.setId ?? '',
            metricId: metric.metricId,
            input: metric.input,
            forCollection: metric.forCollection,
          });
        }

        // remove the metric from the map to track which ones are processed
        existingMetricsMap.delete(metric.metricId);
      });
    }

    // remove the set from the map to track which ones are processed
    existingSetsMap.delete(set.setId ?? '');
  });

  // remaining sets in the map need to be removed
  existingSetsMap.forEach((set) => {
    toRemoveSetMetrics.push(set);
  });

  return { toAddSetMetrics, toEditSetMetrics, toRemoveSetMetrics };
};

// compare metrics between the workouts to determine what metric edits need to be made
// CREATE: if metricsData contains an exerciseMetricId that is not present in existingMetrics
// DELETE: if the exerciseMetricId in existingMetrics is not present in metricsData
export const compareMetrics = (
  metricsData: MetricData[],
  existingMetrics: WorkoutExerciseMetric[],
) => {
  const toAddMetrics: MetricData[] = [];
  const toRemoveMetrics: WorkoutExerciseMetric[] = [];

  const existingMetricsMap = new Map(
    existingMetrics
      .filter((m) => m.metricType !== 'sets')
      .map((m) => [m.metricId, m]),
  );

  metricsData.forEach((metric) => {
    if (metric.metricType === 'sets') return;

    const { exerciseMetricId, metricId } = metric;

    if (!exerciseMetricId) {
      // if there is no exerciseMetricId, metric has been added by user
      // check that a user didn't remove the metric and then added it back
      // otherwise add the new metric
      const existingMetricById = existingMetricsMap.get(metricId);

      if (!existingMetricById) {
        toAddMetrics.push(metric);
      }
    }

    // remove the metric from the map to track which ones are processed
    existingMetricsMap.delete(metric.metricId ?? '');
  });

  // remaining metrics in the map need to be removed
  existingMetricsMap.forEach((metric) => {
    if (!metricsData.some((m) => m.metricId === metric.metricId)) {
      toRemoveMetrics.push(metric);
    }
  });

  return { toAddMetrics, toRemoveMetrics };
};

export const findNewWorkoutSectionAndEntry = (
  exerciseOrder: ExerciseOrder[],
  editExerciseId?: string,
) => {
  const workoutExerciseId = editExerciseId ?? NEW_EXERCISE_WORKOUT_ID;
  for (const section of exerciseOrder) {
    const entry = section.entries.find(
      (ex) => ex.workoutExerciseId === workoutExerciseId,
    );
    if (entry) {
      return { newWorkoutSection: section, exerciseSortOrderEntry: entry };
    }
  }
  return { newWorkoutSection: undefined, exerciseSortOrderEntry: undefined };
};
